/**
 * EntityActionMenu - Reusable kebab overflow menu for entity detail pages.
 * Follows the DropdownMenu pattern from ScopeCard.tsx.
 */
import { MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { LucideIcon } from 'lucide-react';

export interface ActionMenuItem {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  variant?: 'default' | 'destructive';
  disabled?: boolean;
  separator?: boolean;
  /**
   * Theme 5b: native-tooltip reason shown on hover. Pair with `disabled` to
   * explain WHY a doomed affordance (e.g. Mark Ready on a $0 invoice) is greyed
   * out instead of letting the user click it and eat a backend 400.
   */
  title?: string;
}

interface EntityActionMenuProps {
  items: ActionMenuItem[];
}

export function EntityActionMenu({ items }: EntityActionMenuProps) {
  if (items.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          // Polish R2 (Dmitri round 2 — M3): WCAG minimum tap target is 44x44.
          // The default size="sm" button on mobile measured 34x32, below the
          // threshold. Force min 44x44 on mobile (default) and revert to a
          // tighter 32x32 desktop sizing at md+ so we don't bloat the
          // surrounding action bar on Christa's laptop view.
          className="min-w-[44px] min-h-[44px] px-2 md:min-w-0 md:min-h-0 md:h-8 md:w-8 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label="More actions"
          data-testid="entity-action-menu-trigger"
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {items.map((item, index) => (
          <div key={item.label}>
            {item.separator && index > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onClick={item.onClick}
              disabled={item.disabled}
              title={item.title}
              className={`focus:bg-accent focus:text-accent-foreground ${item.variant === 'destructive' ? 'text-destructive focus:text-destructive focus:bg-destructive/10' : ''}`}
              data-testid={`action-menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className="h-4 w-4 mr-2" />
              {item.label}
            </DropdownMenuItem>
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
